import { cn } from '@/lib/utils'
import { BandReveal, InteractiveDots, ScrollReveal, TextReveal } from '@/components/motion'

/**
 * Closing call-to-action band from the page-layout spec: a full-bleed colour
 * band with the static halftone, a left-aligned h1-size heading (pass <br />
 * for forced line breaks), then the actions and a 45ch paragraph side by side
 * on one row. Actions are passed in so a page can use links or buttons.
 */
export function CtaBand({
  title,
  description,
  actions,
  tone = 'blue',
  className,
}: {
  title: React.ReactNode
  description?: React.ReactNode
  actions: React.ReactNode
  tone?: 'blue' | 'purple'
  className?: string
}) {
  return (
    <BandReveal>
      <section
        className={cn(
          'landing-section-lg relative overflow-hidden text-white',
          tone === 'blue' ? 'bg-[var(--color-landing-accent)]' : 'bg-[var(--color-landing-secondary)]',
          className
        )}
      >
        <InteractiveDots />
        <div className="landing-container relative z-10">
          <TextReveal as="h2" className="landing-text-hero max-w-[22ch] text-white">
            {title}
          </TextReveal>
          <ScrollReveal direction="none" delay={0.2}>
            <div className="mt-12 flex flex-col gap-8 md:mt-16 md:flex-row md:items-center md:gap-16">
              <div className="flex flex-wrap items-center gap-4">{actions}</div>
              {description && (
                <p className="max-w-[45ch] landing-text-body-lg text-white/85">{description}</p>
              )}
            </div>
          </ScrollReveal>
        </div>
      </section>
    </BandReveal>
  )
}

/** Pink solid button for blue bands (spec: blue band, pink button). */
export const pinkCtaClass =
  'bg-[#ff00f2] border-[#ff00f2] text-white hover:text-[var(--color-landing-primary)] focus-visible:text-[var(--color-landing-primary)]'
/** Blue solid button for purple or pink bands. */
export const blueCtaClass =
  'bg-[var(--color-landing-accent)] border-[var(--color-landing-accent)] text-white hover:text-[var(--color-landing-primary)] focus-visible:text-[var(--color-landing-primary)]'
